import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import "./UserDetails.scss";
import {
  FaGamepad,
  FaMoneyBillWave,
  FaMoneyCheckAlt,
  FaUsers,
  FaGift,
  FaExclamationTriangle,
  FaIdCard,
  FaSpinner,
  FaPlane,
  FaDragon,
} from "react-icons/fa";
import {
  FiAlertCircle, FiRefreshCw, FiUserX, FiUser, FiPhone, FiSmartphone,
  FiMail, FiCode, FiUserPlus, FiCircle, FiShield, FiLock, FiCreditCard,
  FiZoomIn, FiImage, FiCpu, FiGlobe, FiCalendar, FiClock, FiSearch,
  FiInbox, FiChevronLeft, FiChevronRight, FiUsers
} from "react-icons/fi";
import { userDetails } from "../../api/endpoints";

const Userdetails = () => {
  const { userId } = useParams();
  console.log("UserDetails Component Rendered with userId:", userId);
  const [activeTab, setActiveTab] = useState("game");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  // ✅ Helper function to get image URL
  // ✅ FIXED: Helper function to get image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;

    // If already a complete URL
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }

    // If it's a base64 string
    if (imagePath.startsWith("data:image")) {
      return imagePath;
    }

    // If it's a relative path starting with /
    if (imagePath.startsWith("/")) {
      return imagePath;
    }

    // 🔥 CRITICAL: If it's just a filename, prepend full URL
    // Using the base URL from your server
    const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
    return `${baseUrl}/uploads/kyc/${imagePath}`;
  };

  // ✅ Handle image click for zoom
  const handleImageClick = (imageSrc) => {
    if (imageSrc) {
      setSelectedImage(imageSrc);
    }
  };

  // ✅ Handle image error - show fallback
  const handleImageError = (e, imagePath) => {
    e.target.onerror = null;
    e.target.style.display = "none";
    const parent = e.target.parentElement;
    const fallback = document.createElement("div");
    fallback.className = "no-image";
    fallback.innerHTML = `
            <span class="no-image-mark">Image unavailable</span>
            <span>${imagePath?.split("/").pop() || "Image"}</span>
        `;
    parent.appendChild(fallback);
  };

  useEffect(() => {
    const fetchUserData = async () => {
      console.log("=== UserDetails Component Mounted ===");
      console.log("Received userId prop:", userId);
      console.log("Type of userId:", typeof userId);
      console.log(
        "Is userId valid?",
        userId && userId !== "undefined" && userId !== "null",
      );

      if (
        !userId ||
        userId === "undefined" ||
        userId === "null" ||
        userId === ""
      ) {
        console.error("Invalid userId received:", userId);
        setError("User ID is required. Please check the URL.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await userDetails(userId);

        console.log("API Response:", response);

        if (response.success) {
          setUser(response.data);
          console.log("User data set successfully");
        } else {
          setError(response.message || "Failed to fetch user data");
        }
      } catch (err) {
        console.error("Error fetching user details:", err);
        console.error("Error response:", err.response?.data);
        console.error("Error status:", err.response?.status);

        if (err.response?.status === 401) {
          setError("Unauthorized. Please login again.");
        } else if (err.response?.status === 404) {
          setError("User not found. Please check the user ID.");
        } else if (err.response?.status === 400) {
          setError(err.response?.data?.message || "Invalid user ID format");
        } else {
          setError(
            err.response?.data?.message ||
              "Error fetching user details. Please try again.",
          );
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId]);

  // Get current data based on active tab
  const getCurrentData = () => {
    if (!user) return [];
    switch (activeTab) {
      case "deposit":
        return user.depositHistory || [];
      case "withdrawal":
        return user.withdrawHistory || [];
      case "bonus":
        return user.bonusHistory || [];
      case "penalty":
        return user.penaltyHistory || [];
      case "game":
        return user.betHistory || [];
      case "aviator":
        return user.aviatorbets || [];
      case "kyc":
        return user.kyc ? [user.kyc] : [];
      default:
        return [];
    }
  };

  const currentData = getCurrentData();
  const totalEntries = currentData.length;

  // Filter data based on search term
  const filteredData = currentData.filter((item) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return JSON.stringify(item).toLowerCase().includes(searchLower);
  });

  // Pagination logic
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredData.slice(
    indexOfFirstEntry,
    indexOfLastEntry,
  );

  const tabs = [
    { id: "game", label: "Dragon Tiger", icon: <FaDragon /> },
    { id: "aviator", label: "Aviator", icon: <FaPlane /> },
    { id: "deposit", label: "Deposit History", icon: <FaMoneyBillWave /> },
    {
      id: "withdrawal",
      label: "Withdrawal History",
      icon: <FaMoneyCheckAlt />,
    },
    { id: "referral", label: "Referral History", icon: <FaUsers /> },
    { id: "bonus", label: "Bonus", icon: <FaGift /> },
    { id: "penalty", label: "Penalty", icon: <FaExclamationTriangle /> },
    { id: "kyc", label: "KYC", icon: <FaIdCard /> },
  ];

  // Wallet items from user.wallet object
  const walletItems = [
    {
      label: "Deposit Balance",
      value: user?.wallet?.depositBalance || 0,
      color: "success",
    },
    {
      label: "Winning Balance",
      value: user?.wallet?.winningBalance || 0,
      color: "warning",
    },
    {
      label: "Bonus Balance",
      value: user?.wallet?.bonusBalance || 0,
      color: "primary",
    },
    {
      label: "Cashback Balance",
      value: user?.wallet?.cashbackBalance || 0,
      color: "info",
    },
    {
      label: "Locked Balance",
      value: user?.wallet?.lockedBalance || 0,
      color: "secondary",
    },
  ];

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "N/A";
    }
  };

  // Get status color class
  const getStatusClass = (status) => {
    if (!status) return "";
    const statusMap = {
      active: "active",
      inactive: "inactive",
      verified: "verified",
      pending: "pending",
      rejected: "rejected",
      approved: "approved",
      success: "success",
      failure: "failure",
      win: "win",
      lost: "lost",
      cashed_out: "cashed_out",
      cashedout: "cashed_out",
    };
    return statusMap[status?.toLowerCase()] || status?.toLowerCase() || "";
  };

  // Get bet result class
  const getResultClass = (result) => {
    if (!result) return "";
    return result?.toLowerCase() || "";
  };

  // Render loading state
  if (loading) {
    return (
      <div className="userdetailslist">
        <div className="loading-state">
          <FaSpinner className="spinner" />
          <p>Loading user details...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="userdetailslist">
        <div className="error-state">
          <FiAlertCircle />
          <h4>Error</h4>
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="retry-btn"
          >
            <FiRefreshCw /> Retry
          </button>
        </div>
      </div>
    );
  }

  // Render if no user data
  if (!user) {
    return (
      <div className="userdetailslist">
        <div className="empty-state">
          <FiUserX />
          <h4>No User Data</h4>
          <p>User not found or no data available.</p>
        </div>
      </div>
    );
  }

  // Render table columns based on active tab
  const renderTableHeaders = () => {
    switch (activeTab) {
      case "deposit":
        return (
          <tr>
            <th>ID</th>
            <th>Amount</th>
            <th>Payment Method</th>
            <th>UTR Number</th>
            <th>Status</th>
            <th>Date</th>
            <th>Remark</th>
          </tr>
        );
      case "withdrawal":
        return (
          <tr>
            <th>ID</th>
            <th>Amount</th>
            <th>Type</th>
            <th>UPI ID</th>
            <th>Account Holder</th>
            <th>Status</th>
            <th>Date</th>
          </tr>
        );
      case "bonus":
        return (
          <tr>
            <th>ID</th>
            <th>Amount</th>
            <th>Payment Method</th>
            <th>Status</th>
            <th>Date</th>
            <th>Remark</th>
          </tr>
        );
      case "penalty":
        return (
          <tr>
            <th>ID</th>
            <th>Amount</th>
            <th>Withdraw Type</th>
            <th>Status</th>
            <th>Date</th>
          </tr>
        );
      case "game":
        return (
          <tr>
            <th>ID</th>
            <th>Game</th>
            <th>Selection</th>
            <th>Amount</th>
            <th>Win Amount</th>
            <th>Profit</th>
            <th>Status</th>
            <th>Result</th>
            <th>Round #</th>
            <th>Date</th>
          </tr>
        );
      case "aviator":
        return (
          <tr>
            <th>ID</th>
            <th>Amount</th>
            <th>Bet Type</th>
            <th>Status</th>
            <th>Cashed Out</th>
            <th>Cashout Multiplier</th>
            <th>Win Amount</th>
            <th>Round #</th>
            <th>Crash Point</th>
            <th>Date</th>
          </tr>
        );
      case "kyc":
        return (
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Document Type</th>
            <th>Document Number</th>
            <th>Status</th>
            <th>Date of Birth</th>
            <th>Verified Date</th>
          </tr>
        );
      default:
        return null;
    }
  };

  const renderTableRow = (item) => {
    switch (activeTab) {
      case "deposit":
        return (
          <tr key={item._id}>
            <td className="id-cell">{item._id?.slice(-6) || "N/A"}</td>
            <td>₹{item.amount || 0}</td>
            <td>{item.paymentMethod || "N/A"}</td>
            <td>{item.utrNumber || "N/A"}</td>
            <td>
              <span className={`table-status ${getStatusClass(item.status)}`}>
                {item.status || "N/A"}
              </span>
            </td>
            <td>{formatDate(item.createdAt)}</td>
            <td>{item.remark || "N/A"}</td>
          </tr>
        );
      case "withdrawal":
        return (
          <tr key={item._id}>
            <td className="id-cell">{item._id?.slice(-6) || "N/A"}</td>
            <td>₹{item.amount || 0}</td>
            <td>{item.withdrawType || "N/A"}</td>
            <td>{item.upiId || "N/A"}</td>
            <td>{item.accountHolderName || "N/A"}</td>
            <td>
              <span className={`table-status ${getStatusClass(item.status)}`}>
                {item.status || "N/A"}
              </span>
              {item.rejectedReason && (
                <div className="rejection-reason">{item.rejectedReason}</div>
              )}
            </td>
            <td>{formatDate(item.createdAt)}</td>
          </tr>
        );
      case "bonus":
        return (
          <tr key={item._id}>
            <td className="id-cell">{item._id?.slice(-6) || "N/A"}</td>
            <td>₹{item.amount || 0}</td>
            <td>{item.paymentMethod || "N/A"}</td>
            <td>
              <span className={`table-status ${getStatusClass(item.status)}`}>
                {item.status || "N/A"}
              </span>
            </td>
            <td>{formatDate(item.createdAt)}</td>
            <td>{item.remark || "N/A"}</td>
          </tr>
        );
      case "penalty":
        return (
          <tr key={item._id}>
            <td className="id-cell">{item._id?.slice(-6) || "N/A"}</td>
            <td>₹{item.amount || 0}</td>
            <td>{item.withdrawType || "N/A"}</td>
            <td>
              <span className={`table-status ${getStatusClass(item.status)}`}>
                {item.status || "N/A"}
              </span>
            </td>
            <td>{formatDate(item.createdAt)}</td>
          </tr>
        );
      case "game":
        return (
          <tr key={item._id}>
            <td className="id-cell">{item._id?.slice(-6) || "N/A"}</td>
            <td>
              <span className="game-badge">{item.game || "N/A"}</span>
            </td>
            <td>
              <span
                className={`selection-badge ${item.selection?.toLowerCase() || ""}`}
              >
                {item.selection || "N/A"}
              </span>
            </td>
            <td>₹{item.amount || 0}</td>
            <td>₹{item.winAmount || 0}</td>
            <td
              className={
                item.profit >= 0 ? "profit-positive" : "profit-negative"
              }
            >
              ₹{item.profit || 0}
            </td>
            <td>
              <span
                className={`table-status ${item.status?.toLowerCase() || ""}`}
              >
                {item.status || "N/A"}
              </span>
            </td>
            <td>
              <span className={`result-badge ${getResultClass(item.result)}`}>
                {item.result || "N/A"}
              </span>
            </td>
            <td>{item.round?.roundNumber || "N/A"}</td>
            <td>{formatDate(item.createdAt)}</td>
          </tr>
        );
      case "aviator":
        return (
          <tr key={item._id}>
            <td className="id-cell">{item._id?.slice(-6) || "N/A"}</td>
            <td>₹{item.amount || 0}</td>
            <td>
              <span
                className={`bet-type-badge ${item.betType?.toLowerCase() || ""}`}
              >
                {item.betType || "MANUAL"}
              </span>
            </td>
            <td>
              <span className={`table-status ${getStatusClass(item.status)}`}>
                {item.status || "PENDING"}
              </span>
            </td>
            <td>
              <span
                className={`cashout-status ${item.cashedOut ? "yes" : "no"}`}
              >
                {item.cashedOut ? "Yes" : "No"}
              </span>
            </td>
            <td>
              {item.cashoutMultiplier ? `${item.cashoutMultiplier}x` : "N/A"}
            </td>
            <td>₹{item.winAmount || 0}</td>
            <td>{item.round?.roundNumber || "N/A"}</td>
            <td>
              <span className="crash-point">
                {item.round?.crashPoint ? `${item.round.crashPoint}x` : "N/A"}
              </span>
            </td>
            <td>{formatDate(item.createdAt)}</td>
          </tr>
        );
      case "kyc":
        return (
          <tr key={item._id || "kyc"}>
            <td>{item.fullName || "N/A"}</td>
            <td>{item.email || "N/A"}</td>
            <td>{item.documentType || "N/A"}</td>
            <td>{item.documentNumber || "N/A"}</td>
            <td>
              <span
                className={`table-status ${getStatusClass(item.verificationStatus)}`}
              >
                {item.verificationStatus || "N/A"}
              </span>
              {item.rejectionReason && (
                <div className="rejection-reason">{item.rejectionReason}</div>
              )}
            </td>
            <td>{item.dateOfBirth ? formatDate(item.dateOfBirth) : "N/A"}</td>
            <td>{item.verifiedDate ? formatDate(item.verifiedDate) : "N/A"}</td>
          </tr>
        );
      default:
        return null;
    }
  };

  return (
    <div className="userdetailslist">
      {/* ✅ Image Modal */}
      {selectedImage && (
        <div className="image-modal" onClick={() => setSelectedImage(null)}>
          <span className="close-btn">&times;</span>
          <img src={selectedImage} alt="Document" />
        </div>
      )}

      <div className="user-details-container">
        {/* User Profile Card */}
        <div className="card profile-card">
          <div className="card-header p-3">
            <div className="header-content">
              <FiUser />
              <h4>User Details</h4>
            </div>
            <span
              className={`status-badge ${user.status?.toLowerCase() || ""}`}
            >
              <span className="status-dot"></span>
              {user.status || "N/A"}
            </span>
          </div>

          <div className="card-body">
            {/* Profile Section */}
            <div className="profile-section">
              <div className="avatar-container">
                <div className="avatar">
                  {user.kyc?.fullName
                    ? user.kyc.fullName.charAt(0).toUpperCase()
                    : user.fullName
                      ? user.fullName.charAt(0).toUpperCase()
                      : "U"}
                </div>
                <div className="online-indicator"></div>
              </div>
              <div className="profile-info">
                <h3>{user.kyc?.fullName || user.fullName || "N/A"}</h3>
                <p className="user-mobile">
                  <FiPhone /> {user.mobile || "N/A"}
                </p>
                <p className="user-id">
                  <span className="id-label">ID:</span> {user._id || "N/A"}
                </p>
              </div>
            </div>

            {/* Basic Details */}
            <div className="details-grid">
              <div className="detail-item">
                <label>
                  <FiUser /> Full Name
                </label>
                <div className="detail-value">
                  {user.kyc?.fullName || user.fullName || "N/A"}
                </div>
              </div>
              <div className="detail-item">
                <label>
                  <FiSmartphone /> Mobile Number
                </label>
                <div className="detail-value">{user.mobile || "N/A"}</div>
              </div>
              <div className="detail-item">
                <label>
                  <FiMail /> Email
                </label>
                <div className="detail-value">{user.email || "N/A"}</div>
              </div>
              <div className="detail-item">
                <label>
                  <FiCode /> Referral Code
                </label>
                <div className="detail-value referral-code">
                  {user.referralCode || "N/A"}
                </div>
              </div>
              <div className="detail-item">
                <label>
                  <FiUserPlus /> Referred By
                </label>
                <div className="detail-value">{user.referredBy || "N/A"}</div>
              </div>
              <div className="detail-item">
                <label>
                  <FiCircle /> Account Status
                </label>
                <div className="detail-value">
                  <span
                    className={`status-text ${user.status?.toLowerCase() || ""}`}
                  >
                    {user.status || "N/A"}
                  </span>
                </div>
              </div>
              {/* <div className="detail-item">
                <label>
                  <FiShield /> KYC Status
                </label>
                <div className="detail-value">
                  <span
                    className={`status-text ${user.isKycVerified?.toLowerCase() || ""}`}
                  >
                    {user.isKycVerified || "N/A"}
                  </span>
                </div>
              </div> */}
              {/* <div className="detail-item">
                <label>
                  <FiLock /> PIN Created
                </label>
                <div className="detail-value">
                  <span
                    className={`status-text ${user.pinCreated ? "active" : "inactive"}`}
                  >
                    {user.pinCreated ? "Yes" : "No"}
                  </span>
                </div>
              </div> */}
            </div>

            {/* Wallet Section */}
            <div className="section">
              <h5 className="section-title">
                <FiCreditCard /> Wallet Details
              </h5>
              <div className="wallet-grid">
                {walletItems.map((item, index) => (
                  <div key={index} className={`wallet-card ${item.color}`}>
                    <div className="wallet-label">{item.label}</div>
                    <div className="wallet-value">
                      ₹{item.value.toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ✅ KYC Section with FIXED Images */}
            {user.kyc && (
              <div className="section">
                <h5 className="section-title">
                  <FiShield /> KYC Details
                </h5>
                <div className="kyc-details-grid">
                  <div className="detail-item">
                    <label>KYC Status</label>
                    <span
                      className={`kyc-status ${user.kyc.verificationStatus?.toLowerCase() || ""}`}
                    >
                      {user.kyc.verificationStatus || "N/A"}
                    </span>
                    {user.kyc.rejectionReason && (
                      <div className="rejection-reason">
                        <FiAlertCircle />{" "}
                        {user.kyc.rejectionReason}
                      </div>
                    )}
                  </div>
                  <div className="detail-item">
                    <label>Name</label>
                    <div className="detail-value">
                      {user.kyc.fullName || "N/A"}
                    </div>
                  </div>
                  <div className="detail-item">
                    <label>Email</label>
                    <div className="detail-value">
                      {user.kyc.email || "N/A"}
                    </div>
                  </div>
                  <div className="detail-item">
                    <label>Mobile Number</label>
                    <div className="detail-value">
                      {user.kyc.mobileNumber || "N/A"}
                    </div>
                  </div>
                  <div className="detail-item">
                    <label>Document Type</label>
                    <div className="detail-value">
                      {user.kyc.documentType || "N/A"}
                    </div>
                  </div>
                  <div className="detail-item">
                    <label>Document Number</label>
                    <div className="detail-value">
                      {user.kyc.documentNumber || "N/A"}
                    </div>
                  </div>
                  <div className="detail-item">
                    <label>Date of Birth</label>
                    <div className="detail-value">
                      {user.kyc.dateOfBirth
                        ? formatDate(user.kyc.dateOfBirth)
                        : "N/A"}
                    </div>
                  </div>
                  <div className="detail-item">
                    <label>Verified By</label>
                    <div className="detail-value">
                      {user.kyc.verifiedBy || "N/A"}
                    </div>
                  </div>
                </div>

                {/* ✅ FIXED: Document Images with proper image rendering */}
                {/* ✅ FIXED: Document Images with proper image rendering */}
                <div className="document-images">
                  {user.kyc?.aadhaarFrontImage && (
                    <div className="doc-image-card">
                      <div className="doc-label">Aadhaar Front</div>
                      <div className="image-container">
                        <img
                          src={`http://localhost:5000/uploads/kyc/${user.kyc.aadhaarFrontImage}`}
                          alt="Aadhaar Front"
                          className="doc-image"
                          onClick={() =>
                            handleImageClick(
                              `http://localhost:5000/uploads/kyc/${user.kyc.aadhaarFrontImage}`,
                            )
                          }
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = "none";
                            const parent = e.target.parentElement;
                            const fallback = document.createElement("div");
                            fallback.className = "no-image";
                            fallback.innerHTML = `
                            <span class="no-image-mark">Image unavailable</span>
                            <span>${user.kyc.aadhaarFrontImage}</span>
                        `;
                            parent.appendChild(fallback);
                          }}
                          loading="lazy"
                        />
                        <div
                          className="image-overlay"
                          onClick={() =>
                            handleImageClick(
                              `http://localhost:5000/uploads/kyc/${user.kyc.aadhaarFrontImage}`,
                            )
                          }
                        >
                          <FiZoomIn />
                        </div>
                      </div>
                    </div>
                  )}

                  {user.kyc?.aadhaarBackImage && (
                    <div className="doc-image-card">
                      <div className="doc-label">Aadhaar Back</div>
                      <div className="image-container">
                        <img
                          src={`http://localhost:5000/uploads/kyc/${user.kyc.aadhaarBackImage}`}
                          alt="Aadhaar Back"
                          className="doc-image"
                          onClick={() =>
                            handleImageClick(
                              `http://localhost:5000/uploads/kyc/${user.kyc.aadhaarBackImage}`,
                            )
                          }
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = "none";
                            const parent = e.target.parentElement;
                            const fallback = document.createElement("div");
                            fallback.className = "no-image";
                            fallback.innerHTML = `
                            <span class="no-image-mark">Image unavailable</span>
                            <span>${user.kyc.aadhaarBackImage}</span>
                        `;
                            parent.appendChild(fallback);
                          }}
                          loading="lazy"
                        />
                        <div
                          className="image-overlay"
                          onClick={() =>
                            handleImageClick(
                              `http://localhost:5000/uploads/kyc/${user.kyc.aadhaarBackImage}`,
                            )
                          }
                        >
                          <FiZoomIn />
                        </div>
                      </div>
                    </div>
                  )}

                  {user.kyc?.panImage && (
                    <div className="doc-image-card">
                      <div className="doc-label">PAN Card</div>
                      <div className="image-container">
                        <img
                          src={`http://localhost:5000/uploads/kyc/${user.kyc.panImage}`}
                          alt="PAN Card"
                          className="doc-image"
                          onClick={() =>
                            handleImageClick(
                              `http://localhost:5000/uploads/kyc/${user.kyc.panImage}`,
                            )
                          }
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = "none";
                            const parent = e.target.parentElement;
                            const fallback = document.createElement("div");
                            fallback.className = "no-image";
                            fallback.innerHTML = `
                            <span class="no-image-mark">Image unavailable</span>
                            <span>${user.kyc.panImage}</span>
                        `;
                            parent.appendChild(fallback);
                          }}
                          loading="lazy"
                        />
                        <div
                          className="image-overlay"
                          onClick={() =>
                            handleImageClick(
                              `http://localhost:5000/uploads/kyc/${user.kyc.panImage}`,
                            )
                          }
                        >
                          <FiZoomIn />
                        </div>
                      </div>
                    </div>
                  )}

                  {user.kyc?.documentSelfie && (
                    <div className="doc-image-card">
                      <div className="doc-label">Selfie</div>
                      <div className="image-container">
                        <img
                          src={`http://localhost:5000/uploads/kyc/${user.kyc.documentSelfie}`}
                          alt="Selfie"
                          className="doc-image"
                          onClick={() =>
                            handleImageClick(
                              `http://localhost:5000/uploads/kyc/${user.kyc.documentSelfie}`,
                            )
                          }
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = "none";
                            const parent = e.target.parentElement;
                            const fallback = document.createElement("div");
                            fallback.className = "no-image";
                            fallback.innerHTML = `
                            <span class="no-image-mark">Image unavailable</span>
                            <span>${user.kyc.documentSelfie}</span>
                        `;
                            parent.appendChild(fallback);
                          }}
                          loading="lazy"
                        />
                        <div
                          className="image-overlay"
                          onClick={() =>
                            handleImageClick(
                              `http://localhost:5000/uploads/kyc/${user.kyc.documentSelfie}`,
                            )
                          }
                        >
                          <FiZoomIn />
                        </div>
                      </div>
                    </div>
                  )}

                  {!user.kyc?.aadhaarFrontImage &&
                    !user.kyc?.aadhaarBackImage &&
                    !user.kyc?.panImage &&
                    !user.kyc?.documentSelfie && (
                      <div className="no-documents">
                        <FiImage />
                        <span>No documents uploaded</span>
                      </div>
                    )}
                </div>
              </div>
            )}

            {/* Device Info Section */}
            <div className="section">
              <h5 className="section-title">
                <FiSmartphone /> Device & Login Info
              </h5>
              <div className="device-grid">
                <div className="detail-item">
                  <label>
                    <FiCpu /> Device ID
                  </label>
                  <div className="detail-value device-id">
                    {user.deviceId || "N/A"}
                  </div>
                </div>
                <div className="detail-item">
                  <label>
                    <FiGlobe /> Last Login IP
                  </label>
                  <div className="detail-value">
                    {user.lastLoginIp || "N/A"}
                  </div>
                </div>
                <div className="detail-item">
                  <label>
                    <FiCalendar /> Account Created
                  </label>
                  <div className="detail-value">
                    {formatDate(user.createdAt)}
                  </div>
                </div>
                <div className="detail-item">
                  <label>
                    <FiClock /> Last Login
                  </label>
                  <div className="detail-value">
                    {formatDate(user.lastLogin)}
                  </div>
                </div>
                <div className="detail-item">
                  <label>
                    <FiRefreshCw /> Last Updated
                  </label>
                  <div className="detail-value">
                    {formatDate(user.updatedAt)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* History Tabs Section */}
        <div className="card history-card">
          <div className="tabs-container">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => {
                  setActiveTab(tab.id);
                  setCurrentPage(1);
                  setSearchTerm("");
                }}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          <div className="tab-content">
            {activeTab !== "referral" && (
              <div className="kyc-table-container">
                <div className="table-controls">
                  <div className="entries-control">
                    <span>Show</span>
                    <select
                      value={entriesPerPage}
                      onChange={(e) => {
                        setEntriesPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="entries-select"
                    >
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                    <span>entries</span>
                  </div>
                  <div className="search-box">
                    <FiSearch />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="data-table">
                    <thead>{renderTableHeaders()}</thead>
                    <tbody>
                      {currentEntries.length > 0 ? (
                        currentEntries.map((item) => renderTableRow(item))
                      ) : (
                        <tr>
                          <td colSpan="10" className="empty-state">
                            <div className="empty-state-content">
                              <FiInbox />
                              <p>
                                No{" "}
                                {activeTab === "game"
                                  ? "Dragon Tiger"
                                  : activeTab === "aviator"
                                    ? "Aviator"
                                    : ""}{" "}
                                data available
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {totalEntries > 0 && (
                  <div className="table-footer">
                    <div className="showing-info">
                      Showing {indexOfFirstEntry + 1} to{" "}
                      {Math.min(indexOfLastEntry, totalEntries)} of{" "}
                      {totalEntries} entries
                    </div>
                    <div className="pagination">
                      <button
                        className="pagination-btn"
                        disabled={currentPage === 1}
                        onClick={() =>
                          setCurrentPage((prev) => Math.max(prev - 1, 1))
                        }
                      >
                        <FiChevronLeft /> Previous
                      </button>
                      <div className="page-numbers">
                        <span className="current-page">{currentPage}</span>
                      </div>
                      <button
                        className="pagination-btn"
                        disabled={
                          currentPage ===
                          Math.ceil(totalEntries / entriesPerPage)
                        }
                        onClick={() =>
                          setCurrentPage((prev) =>
                            Math.min(
                              prev + 1,
                              Math.ceil(totalEntries / entriesPerPage),
                            ),
                          )
                        }
                      >
                        Next <FiChevronRight />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "referral" && (
              <div className="empty-state">
                <FiUsers />
                <h4>Referral History</h4>
                <p>No referral data available for this user.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Userdetails;
